type InspectionScopedBindings = {
  inspectionId: string;
  templateId?: string;
  checklistId?: string;
  sectionId?: string;
  protocolId?: string;
};

type InspectionDocumentUrlBindings = InspectionScopedBindings & {
  _id?: string;
};

export class ConflictingInspectionDocumentBindingsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConflictingInspectionDocumentBindingsError';
  }
}

function hasBindingId(value: string | undefined): value is string {
  return Boolean(value);
}

function warnConflictingInspectionDocumentBindings(
  bindings: InspectionScopedBindings,
  conflicts: string[]
): void {
  if (conflicts.length === 0) {
    return;
  }

  // eslint-disable-next-line no-console
  console.warn(
    '[inspectionDocumentBindings] Conflicting document bindings detected',
    { bindings, conflicts }
  );
}

export function assertValidInspectionScopedBindings(
  document: InspectionScopedBindings
): void {
  const { templateId, checklistId, sectionId, protocolId } = document;

  if (
    hasBindingId(protocolId) &&
    hasBindingId(sectionId) &&
    !hasBindingId(templateId)
  ) {
    throw new ConflictingInspectionDocumentBindingsError(
      'Document cannot be bound to protocol and section at once.'
    );
  }

  const conflicts: string[] = [];

  if (
    hasBindingId(templateId) &&
    hasBindingId(protocolId) &&
    hasBindingId(sectionId)
  ) {
    conflicts.push('templateId+protocolId+sectionId');
  } else if (hasBindingId(templateId) && hasBindingId(protocolId)) {
    conflicts.push('templateId+protocolId');
  }

  if (
    hasBindingId(templateId) &&
    hasBindingId(checklistId) &&
    hasBindingId(sectionId)
  ) {
    conflicts.push('templateId+checklistId+sectionId');
  }

  warnConflictingInspectionDocumentBindings(document, conflicts);
}

function createDocumentUrlPart(documentId?: string): string {
  return documentId ? `/document/${documentId}` : '/document';
}

export function createInspectionScopedEntityPath(
  document: InspectionScopedBindings
): string {
  assertValidInspectionScopedBindings(document);

  const {
    inspectionId,
    templateId,
    checklistId,
    sectionId,
    protocolId,
  } = document;

  if (templateId) {
    if (checklistId) {
      return `/inspection/${inspectionId}/checklist/${checklistId}`;
    }
    if (sectionId) {
      return `/inspection/${inspectionId}/template/${templateId}/section/${sectionId}`;
    }
    return `/inspection/${inspectionId}/template/${templateId}`;
  }

  if (protocolId) {
    return `/inspection/${inspectionId}/protocolItem/${protocolId}/complianceCheck`;
  }

  if (sectionId) {
    return `/inspection/${inspectionId}/section/${sectionId}`;
  }

  return `/inspection/${inspectionId}`;
}

export function createInspectionScopedDocumentPath(
  document: InspectionDocumentUrlBindings
): string {
  return `${createInspectionScopedEntityPath(document)}${createDocumentUrlPart(
    document._id
  )}`;
}
